import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import './Products.css';
import './BulkUploadProducts.css';
import { useNotification } from '../../context/NotificationContext';

// Icons
const ArrowLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const AlertCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

const FileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

function BulkUploadProducts() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [filename, setFilename] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [templateColumns, setTemplateColumns] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef();
  const { getAccessTokenSilently } = useAuth0();
  const notification = useNotification();

  // Fetch template on mount
  React.useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default template columns (used as fallback if server doesn't have all fields)
  const defaultTemplateColumns = [
    { key: 'retailerSkuId', label: 'SKU ID', required: true, description: 'Unique product identifier' },
    { key: 'productName', label: 'Product Name', required: true, description: 'Name of the product' },
    { key: 'productDescription', label: 'Description', required: false, description: 'Product description' },
    { key: 'brand', label: 'Brand', required: false, description: 'Product brand name' },
    { key: 'variantId', label: 'Variant ID', required: false, description: 'Variant identifier' },
    { key: 'category', label: 'Category', required: false, description: 'Product category (e.g., Grocery, Dairy, Produce)' },
    { key: 'originalPrice', label: 'Original Price', required: true, description: 'Regular price of the product' },
    { key: 'promoPrice', label: 'Promo Price', required: false, description: 'Promotional/sale price' },
    { key: 'promoStartDate', label: 'Promo Start Date', required: false, description: 'Start date for promotion (YYYY-MM-DD)' },
    { key: 'promoEndDate', label: 'Promo End Date', required: false, description: 'End date for promotion (YYYY-MM-DD)' },
    { key: 'promoType', label: 'Promo Type', required: false, description: 'Type of promotion (PRICE_DISCOUNT, PERCENTAGE_DISCOUNT, BOGO)' },
    { key: 'limitQty', label: 'Limit Quantity', required: false, description: 'Maximum quantity per customer' },
    { key: 'priceUnit', label: 'Price Unit', required: false, description: 'Unit of measure (ea., lb., kg, ltr, pack of 2, etc.)' },
    { key: 'packSize', label: 'Pack Size/Weight', required: false, description: 'Product size or weight (e.g., 500g, 1L, 12oz)' },
    { key: 'imageUrl', label: 'Image URL', required: false, description: 'URL to product image' }
  ];

  const fetchTemplate = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/bulk-upload/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Merge server columns with default columns to ensure new fields are included
      const serverColumns = response.data.columns || [];
      const serverKeys = serverColumns.map(c => c.key);
      const missingColumns = defaultTemplateColumns.filter(c => !serverKeys.includes(c.key));
      setTemplateColumns([...serverColumns, ...missingColumns]);
    } catch (error) {
      console.error('Failed to fetch template:', error);
      // Use default columns as fallback
      setTemplateColumns(defaultTemplateColumns);
    }
  };

  const downloadTemplate = () => {
    // Use default columns if templateColumns is empty
    const columns = templateColumns.length > 0 ? templateColumns : defaultTemplateColumns;
    const headers = columns.map(col => col.label);
    const keys = columns.map(col => col.key);

    const sampleRow = {
      retailerSkuId: '12345',
      productName: 'Sample Product',
      productDescription: 'A sample product description',
      brand: 'Sample Brand',
      variantId: '',
      category: 'Grocery',
      originalPrice: '9.99',
      promoPrice: '7.99',
      promoStartDate: '2024-01-01',
      promoEndDate: '2024-01-31',
      promoType: 'PRICE_DISCOUNT',
      limitQty: '4',
      priceUnit: 'ea.',
      packSize: '500g',
      imageUrl: ''
    };

    const sampleData = keys.map(key => sampleRow[key] || '');

    const wb = XLSX.utils.book_new();
    const wsData = [headers, sampleData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = headers.map(() => ({ wch: 20 }));

    XLSX.utils.book_append_sheet(wb, ws, 'Products');

    const instructionsData = [
      ['Bulk Upload Template Instructions'],
      [''],
      ['Required Fields:'],
      ...columns.filter(c => c.required).map(c => [`- ${c.label}: ${c.description}`]),
      [''],
      ['Optional Fields:'],
      ...columns.filter(c => !c.required).map(c => [`- ${c.label}: ${c.description}`]),
      [''],
      ['Promo Types:'],
      ['- PRICE_DISCOUNT: Fixed price discount'],
      ['- PERCENTAGE_DISCOUNT: Percentage off original price'],
      ['- BOGO: Buy one get one'],
      [''],
      ['Price Units:'],
      ['- ea. (each)'],
      ['- lb. (pound)'],
      ['- kg (kilogram)'],
      ['- ltr (liter)'],
      ['- pack of 2, pack of 3, pack of 6, pack of 12'],
      [''],
      ['Notes:'],
      ['- Date format: YYYY-MM-DD'],
      ['- Promo price must be less than original price'],
      ['- If promo price is set, promo dates and type are required']
    ];
    const wsInstructions = XLSX.utils.aoa_to_sheet(instructionsData);
    wsInstructions['!cols'] = [{ wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

    XLSX.writeFile(wb, 'product_upload_template.xlsx');

    notification.success('Template downloaded successfully!');
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFilename(selectedFile.name);
      setValidationResult(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      setFile(droppedFile);
      setFilename(droppedFile.name);
      setValidationResult(null);
    }
  };

  const excelDateToString = (value) => {
    if (!value || value === '') return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') {
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  };

  const parseFile = async (file) => {
    return new Promise((resolve, reject) => {
      const extension = file.name.split('.').pop().toLowerCase();

      if (extension === 'csv') {
        Papa.parse(file, {
          complete: (results) => {
            resolve(mapToProductFormat(results.data));
          },
          header: true,
          skipEmptyLines: true,
          error: reject
        });
      } else if (['xlsx', 'xls'].includes(extension)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const workbook = XLSX.read(e.target.result, { type: 'array', cellDates: false });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: true });
            resolve(mapToProductFormat(jsonData));
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      } else {
        reject(new Error('Unsupported file format'));
      }
    });
  };

  const mapToProductFormat = (data) => {
    const labelToKey = {};
    templateColumns.forEach(col => {
      labelToKey[col.label.toLowerCase()] = col.key;
      labelToKey[col.key.toLowerCase()] = col.key;
    });

    const dateFields = ['promoStartDate', 'promoEndDate'];

    return data.map(row => {
      const product = {};
      Object.keys(row).forEach(key => {
        const normalizedKey = key.toLowerCase().trim();
        const mappedKey = labelToKey[normalizedKey];
        if (mappedKey) {
          if (dateFields.includes(mappedKey)) {
            product[mappedKey] = excelDateToString(row[key]);
          } else {
            product[mappedKey] = row[key];
          }
        }
      });
      return product;
    }).filter(product => Object.keys(product).length > 0);
  };

  const handleValidate = async () => {
    if (!file) {
      notification.error('Please select a file to upload');
      return;
    }

    setIsValidating(true);
    setValidationResult(null);

    try {
      const products = await parseFile(file);

      if (!products || products.length === 0) {
        notification.error('No valid products found in the file');
        setIsValidating(false);
        return;
      }

      const token = await getAccessTokenSilently();
      const response = await axios.post(`${process.env.REACT_APP_SERVER_DOMAIN}/api/bulk-upload/validate`,
        { products },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = response.data;
      setValidationResult(result);

      if (result.summary.valid > 0 && result.summary.errors === 0) {
        notification.success(`All ${result.summary.valid} products are valid!`);
      } else if (result.summary.valid > 0) {
        notification.warning(`${result.summary.valid} valid, ${result.summary.errors} with errors`);
      } else {
        notification.error(`All ${result.summary.errors} products have errors`);
      }
    } catch (error) {
      console.error('Validation error:', error);
      notification.error('Failed to validate products: ' + error.message);
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!validationResult || validationResult.validProducts.length === 0) {
      return;
    }

    setIsUploading(true);

    try {
      const token = await getAccessTokenSilently();
      const response = await axios.post(`${process.env.REACT_APP_SERVER_DOMAIN}/api/bulk-upload/confirm`,
        { products: validationResult.validProducts },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = response.data;

      if (result.summary.success > 0) {
        notification.success(`Successfully uploaded ${result.summary.success} products!`);
        clearForm();
      }

      if (result.summary.failed > 0) {
        const failedErrors = result.results?.failed?.map(f => f.error).join(', ') || 'Unknown error';
        notification.error(`${result.summary.failed} products failed: ${failedErrors}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      notification.error(error.response?.data?.error || 'Failed to upload products');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadErrors = () => {
    if (!validationResult || validationResult.errors.length === 0) {
      return;
    }

    const headers = [...templateColumns.map(col => col.label), 'Errors'];
    const keys = templateColumns.map(col => col.key);

    const errorData = validationResult.errors.map(err => {
      const row = keys.map(key => err.product[key] || '');
      row.push(err.errors.join('; '));
      return row;
    });

    const wb = XLSX.utils.book_new();
    const wsData = [headers, ...errorData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = headers.map(() => ({ wch: 20 }));

    XLSX.utils.book_append_sheet(wb, ws, 'Errors');
    XLSX.writeFile(wb, 'product_upload_errors.xlsx');

    notification.info('Error file downloaded');
  };

  const clearForm = () => {
    setFile(null);
    setFilename('');
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bulk-upload-wizard">
      
      {/* Header - White like AddFlyer */}
      <header className="wizard-header">
        <div className="wizard-header-content">
          <button className="back-btn" onClick={() => navigate('/products')}>
            <ArrowLeftIcon />
          </button>
          <h1 className="wizard-title">Bulk Upload Products</h1>
        </div>
      </header>

      {/* Scrollable Content */}
      <div className="wizard-content">
        <div className="step-container">
          {/* Step 1: Download Template */}
          <div className="form-card" style={{ marginBottom: '24px' }}>
            <h2 className="form-card-title">1. Download Template</h2>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>
              Download our Excel template with all required columns and instructions.
            </p>
            <button className="action-btn primary-outline" onClick={downloadTemplate}>
              <DownloadIcon /> Download Excel Template
            </button>
          </div>

          {/* Step 2: Upload File */}
          <div className="form-card" style={{ marginBottom: '24px' }}>
            <h2 className="form-card-title">2. Upload Your Data</h2>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {!filename ? (
              <div
                className={`drag-drop-area ${dragActive ? 'active' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="drag-drop-icon">
                  <UploadIcon />
                </div>
                <div className="drag-drop-text">
                  <h3>Drop your file here</h3>
                  <p>or click to browse from your device</p>
                </div>
                <span style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px' }}>
                  Supports: CSV, XLSX, XLS
                </span>
              </div>
            ) : (
              <div className="file-selected-modern">
                <div className="file-info">
                  <div className="file-icon-wrapper">
                    <FileIcon />
                  </div>
                  <div className="file-details">
                    <span className="file-name">{filename}</span>
                    <span className="file-status" style={{ color: '#ff8264' }}>Ready to validate</span>
                  </div>
                </div>
                <button
                  className="action-btn primary-outline"
                  onClick={(e) => { e.stopPropagation(); clearForm(); }}
                  style={{ padding: '8px 12px' }}
                >
                  <XIcon /> Remove
                </button>
              </div>
            )}

            {filename && (
              <div style={{ marginTop: '16px' }}>
                <button
                  className="action-btn primary"
                  onClick={handleValidate}
                  disabled={isValidating || !file}
                  style={{ width: '100%' }}
                >
                  {isValidating ? 'Validating...' : 'Validate Products'}
                </button>
              </div>
            )}
          </div>

          {/* Step 3: Review Results */}
          {validationResult && (
            <div className="form-card" style={{ marginBottom: '24px' }}>
              <h2 className="form-card-title">3. Review & Confirm</h2>
              {/* Summary Cards */}
              <div className="summary-cards-modern">
                <div className="summary-card-modern valid">
                  <div className="summary-icon">
                    <CheckCircleIcon />
                  </div>
                  <div className="summary-content">
                    <span className="summary-count">{validationResult.summary.valid}</span>
                    <span className="summary-label">Valid Products</span>
                  </div>
                </div>
                <div className="summary-card-modern errors">
                  <div className="summary-icon">
                    <AlertCircleIcon />
                  </div>
                  <div className="summary-content">
                    <span className="summary-count">{validationResult.summary.errors}</span>
                    <span className="summary-label">With Errors</span>
                  </div>
                </div>
                <div className="summary-card-modern total">
                  <div className="summary-icon">
                    <FileIcon />
                  </div>
                  <div className="summary-content">
                    <span className="summary-count">{validationResult.summary.total}</span>
                    <span className="summary-label">Total Records</span>
                  </div>
                </div>
              </div>

              {/* Valid Products Table */}
              {validationResult.validProducts.length > 0 && (
                <div className="validation-table-section">
                  <h3 className="table-section-title success">
                    <CheckCircleIcon /> Valid Products Ready to Upload
                  </h3>
                  <div className="products-table-container">
                    <table className="products-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>SKU</th>
                          <th>Product Name</th>
                          <th>Category</th>
                          <th>Original Price</th>
                          <th>Promo Price</th>
                          <th>Pack Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResult.validProducts.slice(0, 10).map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.row}</td>
                            <td>{item.product.retailerSkuId}</td>
                            <td>{item.product.productName}</td>
                            <td>{item.product.category || '-'}</td>
                            <td>${item.product.originalPrice}</td>
                            <td>{item.product.promoPrice ? `$${item.product.promoPrice}` : '-'}</td>
                            <td>{item.product.packSize || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {validationResult.validProducts.length > 10 && (
                      <p className="table-note">
                        Showing 10 of {validationResult.validProducts.length} valid products
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Mobile Card View for Valid Products */}
              {validationResult.validProducts.length > 0 && (
                <div className="products-mobile-cards">
                  {validationResult.validProducts.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="product-mobile-card">
                      <div className="product-mobile-card-header">
                        <div className="product-mobile-card-info">
                          <h3>{item.product.productName}</h3>
                          <p>SKU: {item.product.retailerSkuId} {item.product.category && `• ${item.product.category}`}</p>
                        </div>
                      </div>
                      <div className="product-mobile-card-body">
                        <span>
                          <span className="label">Original:</span>
                          <span className="value">${item.product.originalPrice}</span>
                        </span>
                        <span>
                          <span className="label">Promo:</span>
                          <span className="value">{item.product.promoPrice ? `$${item.product.promoPrice}` : '-'}</span>
                        </span>
                        <span>
                          <span className="label">Pack Size:</span>
                          <span className="value">{item.product.packSize || '-'}</span>
                        </span>
                      </div>
                    </div>
                  ))}
                  {validationResult.validProducts.length > 5 && (
                    <p className="table-note">
                      Showing 5 of {validationResult.validProducts.length} valid products
                    </p>
                  )}
                </div>
              )}

              {/* Error Products Table */}
              {validationResult.errors.length > 0 && (
                <div className="validation-table-section" style={{ marginTop: '24px' }}>
                  <div className="table-section-header">
                    <h3 className="table-section-title error">
                      <AlertCircleIcon /> Products with Errors
                    </h3>
                    <button className="action-btn primary-outline" onClick={downloadErrors} style={{ padding: '6px 12px', fontSize: '13px' }}>
                      <DownloadIcon /> Download Errors
                    </button>
                  </div>
                  <div className="products-table-container">
                    <table className="products-table errors-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>SKU</th>
                          <th>Product Name</th>
                          <th>Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResult.errors.slice(0, 10).map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.row}</td>
                            <td>{item.product.retailerSkuId || '-'}</td>
                            <td>{item.product.productName || '-'}</td>
                            <td className="error-cell">
                              <ul>
                                {item.errors.map((err, errIdx) => (
                                  <li key={errIdx}>{err}</li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {validationResult.errors.length > 10 && (
                      <p className="table-note">
                        Showing 10 of {validationResult.errors.length} products with errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Action Bar at Bottom */}
      <div className="action-bar">
        <button className="action-btn secondary" onClick={() => navigate('/products')}>
          Cancel
        </button>
        {validationResult && validationResult.validProducts.length > 0 && (
          <button
            className="action-btn primary"
            onClick={handleConfirmUpload}
            disabled={isUploading}
          >
            {isUploading
              ? 'Uploading...'
              : `Upload ${validationResult.validProducts.length} Products`}
          </button>
        )}
      </div>
    </div>
  );
}

export default BulkUploadProducts;
